import { AgentThreadMessageDetailStatus } from "@/api";
import { AgentThreadMessage } from "@/store/chat";
import { isToolRequestMessage, isToolResponseMessage } from "@/util/message";
import toolStatus from "./tool_status.json";

type ToolStatus = {
  [key: string]: {
    done: string;
    working?: string;
    waiting?: string;
    error?: string;
    cancel?: string;
  };
};

export type ToolStatusType =
  | "working"
  | "done"
  | "waiting"
  | "cancel"
  | "error"
  | "appending";

function normalizeToolName(toolName: string) {
  return toolName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}

export function getToolWaitingText(toolName: string) {
  return normalizeToolName(toolName) + " is waiting your approval";
}

export function getToolWorkingText(toolName: string) {
  return normalizeToolName(toolName) + " is working...";
}

export function getToolErrorText(toolName: string) {
  return normalizeToolName(toolName) + " has failed.";
}

export function getToolSuccessText(toolName: string) {
  return normalizeToolName(toolName) + " has completed.";
}

export function getToolCancelText(toolName: string) {
  return normalizeToolName(toolName) + " cancelled.";
}

export interface ToolStatusResult {
  text: string;
  status: ToolStatusType;
}

export function getToolStatus(message: AgentThreadMessage): ToolStatusResult {
  const messageContent = message.content;

  let text = "";
  let status: ToolStatusType = "working";

  const toolStatusItem = (toolStatus as ToolStatus)[messageContent.func_name!];

  // function call request
  if (isToolRequestMessage(message)) {
    if (message.status === AgentThreadMessageDetailStatus.Done) {
      const defaultWorkingText = getToolWorkingText(messageContent.func_name!);
      text = toolStatusItem?.working || defaultWorkingText;
      status = "working";
    } else if (message.status === AgentThreadMessageDetailStatus.Waiting) {
      const defaultWaitingText = getToolWaitingText(messageContent.func_name!);
      text = toolStatusItem?.waiting || defaultWaitingText;
      status = "waiting";
    }
  }

  // function call response
  if (isToolResponseMessage(message)) {
    if (message.status === AgentThreadMessageDetailStatus.Done) {
      if (messageContent.is_error) {
        const defaultErrorText = getToolErrorText(messageContent.func_name!);
        text = toolStatusItem?.error || defaultErrorText;
        status = "error";
      } else if (messageContent.trigger === "cancel") {
        const defaultCancelText = getToolCancelText(messageContent.func_name!);
        text = toolStatusItem?.cancel || defaultCancelText;
        status = "cancel";
      } else {
        const defaultDoneText = getToolSuccessText(messageContent.func_name!);
        text = toolStatusItem?.done || defaultDoneText;
        status = "done";
      }
    } else if (message.status === AgentThreadMessageDetailStatus.Appending) {
      // When function message is in appending status, show working text
      const defaultWorkingText = getToolWorkingText(messageContent.func_name!);
      text = toolStatusItem?.working || defaultWorkingText;
      status = "working";
    }

    if (messageContent.total && messageContent.total > 0) {
      const progress = messageContent.progress || 0;
      const total = messageContent.total || 0;
      text = `${text} (${progress}/${total})`;
    }
  }

  return { text, status };
}
